import React, { useMemo } from "react";
import AsyncSelect from "react-select/async";
import { X } from "lucide-react";
import Swal from "sweetalert2";
import { configuracoes } from "../../../api/configuracoes";

const formatarOpcao = (cliente) => ({
  value: cliente.id,
  label: `${cliente.nome}${
    cliente.telefone ? ` · ${cliente.telefone}` : cliente.email ? ` · ${cliente.email}` : ""
  }`,
  cliente,
});

const ClientesBloqueadosSelector = ({
  estabelecimentoId,
  value = [],
  onChange,
  disabled = false,
}) => {
  const idsSelecionados = useMemo(
    () => new Set((value || []).map((cliente) => Number(cliente.id))),
    [value]
  );

  const carregarOpcoes = async (termo) => {
    const busca = String(termo || "").trim();
    if (!estabelecimentoId || busca.length < 2) return [];

    try {
      const resposta = await configuracoes.buscarClientes(
        estabelecimentoId,
        busca
      );
      return (resposta.data?.clientes || [])
        .filter((cliente) => !idsSelecionados.has(Number(cliente.id)))
        .map(formatarOpcao);
    } catch {
      return [];
    }
  };

  const adicionarCliente = (opcao) => {
    if (!opcao?.cliente) return;
    const id = Number(opcao.cliente.id);
    if (!id || idsSelecionados.has(id)) return;

    onChange?.([
      ...(value || []),
      {
        id,
        nome: opcao.cliente.nome,
        email: opcao.cliente.email,
        telefone: opcao.cliente.telefone,
        origem: "manual",
      },
    ]);
  };

  const removerCliente = async (cliente) => {
    if (cliente.origem === "automatico") {
      const resultado = await Swal.fire({
        title: "Remover bloqueio automático?",
        html: `<strong>${cliente.nome}</strong> foi bloqueado(a) por excesso de cancelamentos. Deseja liberar novamente?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover bloqueio",
        cancelButtonText: "Manter bloqueado",
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#667085",
        reverseButtons: true,
        focusCancel: true,
      });

      if (!resultado.isConfirmed) return;
    }

    onChange?.(
      (value || []).filter(
        (item) => Number(item.id) !== Number(cliente.id)
      )
    );
  };

  return (
    <div className="clientes-bloqueados">
      <AsyncSelect
        cacheOptions
        defaultOptions={false}
        loadOptions={carregarOpcoes}
        value={null}
        onChange={adicionarCliente}
        placeholder="Buscar cliente por nome, e-mail ou telefone..."
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim().length < 2
            ? "Digite ao menos 2 caracteres"
            : "Nenhum cliente encontrado"
        }
        loadingMessage={() => "Buscando..."}
        isDisabled={disabled || !estabelecimentoId}
        isClearable
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: 48,
            borderRadius: 8,
            borderColor: state.isFocused ? "#ff5a00" : "#d8dee8",
            boxShadow: state.isFocused
              ? "0 0 0 3px rgba(255, 90, 0, 0.12)"
              : "none",
            "&:hover": { borderColor: "#ff5a00" },
          }),
          placeholder: (base) => ({ ...base, color: "#98a2b3" }),
          indicatorSeparator: () => ({ display: "none" }),
        }}
      />

      {(value || []).length > 0 ? (
        <ul className="clientes-bloqueados-lista">
          {(value || []).map((cliente) => (
            <li key={cliente.id} className="clientes-bloqueados-item">
              <span className="clientes-bloqueados-info">
                <strong>{cliente.nome}</strong>
                {cliente.telefone ? ` · ${cliente.telefone}` : ""}
                {cliente.origem === "automatico" && (
                  <span className="clientes-bloqueados-badge">Automático</span>
                )}
              </span>
              <button
                type="button"
                className="clientes-bloqueados-remover"
                onClick={() => removerCliente(cliente)}
                disabled={disabled}
                aria-label={`Remover ${cliente.nome}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="config-nota">Nenhum cliente bloqueado.</p>
      )}
    </div>
  );
};

export default ClientesBloqueadosSelector;
